import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import logo from '../../assets/logo.png';
import { Helmet } from 'react-helmet';
import { CiUser } from 'react-icons/ci';
import styles from './style.module.scss';
import { useMenuData } from '../../menuData';
import { MdOutlineLogout, MdOutlineMessage, MdOutlinePerson, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { BsQuestionOctagon } from 'react-icons/bs';
import { CiLogin } from 'react-icons/ci';
import { useMediaQuery } from 'react-responsive';
import { api } from '../../services/apiClient';

const SidebarOfficial = ({ collapsed, toggleCollapsed }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const [usuarioData, setUsuarioData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState([]);
  const menuData = useMenuData();
  const location = useLocation();
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' });

  const currentPageTitle = menuData.find((item) => item.to === location.pathname)?.pageTitle || 'Tribo da sorte';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/usuario');
        setUsuarioData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Erro ao buscar dados', error);
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const handleLogout = () => {
    logout();
  };

  const handleMenuClick = () => {
    if (isMobile) {
      toggleCollapsed();
    }
  };

  const toggleSubmenu = (key) => {
    setExpandedMenus((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const renderMenuItem = (item) => {
    const active = isActive(item.to);

    return (
      <li key={item.key} className={`${styles.menuItem} ${active ? styles.active : ''}`}>
        {item.to ? (
          <Link to={item.to} className={styles.menuLink} onClick={handleMenuClick}>
            <span className={styles.menuIcon}>{item.icon}</span>
            <span className={styles.menuText}>{item.title}</span>
          </Link>
        ) : (
          <div className={styles.menuLink}>
            <span className={styles.menuIcon}>{item.icon}</span>
            <span className={styles.menuText}>{item.title}</span>
          </div>
        )}
      </li>
    );
  };

  const renderSubMenu = (submenu) => {
    const visibleChildren = submenu.children.filter((child) => child.isVisible);
    if (!submenu.isVisible || visibleChildren.length === 0) {
      return null;
    }

    const isExpanded = expandedMenus.includes(submenu.key);

    return (
      <li key={submenu.key} className={styles.submenuContainer}>
        <div className={`${styles.submenuTrigger} ${isExpanded ? styles.expanded : ''}`} onClick={() => toggleSubmenu(submenu.key)}>
          <span className={styles.menuIcon}>{submenu.icon}</span>
          <span className={styles.menuText}>{submenu.title}</span>
          <span className={styles.expandIcon}>{isExpanded ? <MdExpandLess /> : <MdExpandMore />}</span>
        </div>
        <ul className={`${styles.submenu} ${isExpanded ? styles.expanded : ''}`}>{visibleChildren.map((child) => renderMenuItem(child))}</ul>
      </li>
    );
  };

  const renderMenuItems = () => {
    const filteredItems = menuData.filter((item) => item.isVisible);
    return filteredItems.map((item) => {
      return item.children ? renderSubMenu(item) : renderMenuItem(item);
    });
  };

  const handleRedirect = (url) => {
    window.open(url, '_blank');
  };

  function formatCurrency(value, locale = 'pt-BR', currency = 'BRL') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(value);
  }

  const renderUserInfo = () => {
    if (usuarioData) {
      // Extrair nome do email se o nome for igual ao email
      const displayName = usuarioData.nome === usuarioData.email ? usuarioData.email.split('@')[0] : usuarioData.nome;

      return (
        <div className={styles.userProfile}>
          <div className={styles.userAvatar}>
            <span className={styles.avatarText}>{displayName.charAt(0).toUpperCase()}</span>
            <div className={styles.statusIndicator}></div>
          </div>
          <div className={styles.userDetails}>
            <h3 className={styles.userName}>{displayName}</h3>
            <p className={styles.userEmail}>{usuarioData.email}</p>
            <div className={styles.balanceContainer}>
              <span className={styles.balanceLabel}>Saldo disponível</span>
              <span className={styles.balanceValue}>{formatCurrency(usuarioData.saldo)}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}>
      <Helmet>
        <title>{currentPageTitle}</title>
      </Helmet>

      {/* Logo Container */}
      {!isMobile && (
        <div className={styles.containerLogo}>
          <Link to='/' className={styles.logoLink}>
            <img src={logo || '/placeholder.svg'} alt='logo tribo da sorte' />
          </Link>
        </div>
      )}

      {/* Main Content */}
      <div className={styles.sidebarContent}>
        {/* User Info */}
        {usuarioData && renderUserInfo()}

        {/* Auth Buttons for Mobile */}
        {isMobile && !usuarioData && (
          <div className={styles.authButtonsContainer}>
            <Link to='/login'>
              <button className={styles.buttonAuth} onClick={handleMenuClick}>
                <CiLogin />
                <span>Entrar</span>
              </button>
            </Link>
            <Link to='/cadastrar'>
              <button className={styles.buttonAuth} onClick={handleMenuClick}>
                <CiUser />
                <span>Cadastrar</span>
              </button>
            </Link>
          </div>
        )}

        {/* My Account Button */}
        {usuarioData && (
          <Link to='/minha-conta?tab=padrao' className={styles.linkMinhaConta} onClick={handleMenuClick}>
            <button className={styles.buttonMinhaConta}>
              <MdOutlinePerson />
              <span>Minha conta</span>
            </button>
          </Link>
        )}

        {/* Navigation Menu */}
        <nav className={styles.navigation}>
          <ul className={styles.menuList}>{renderMenuItems()}</ul>
        </nav>
      </div>

      {/* Footer */}
      <div className={styles.sidebarFooter}>
        <button className={styles.buttonOutline} onClick={() => handleRedirect('https://api.whatsapp.com/send?phone=553492118907&text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20informa%C3%A7%C3%B5es.')}>
          <BsQuestionOctagon />
          <span>Fale conosco</span>
        </button>

        {usuarioData ? (
          <button
            className={styles.buttonAuth}
            onClick={() => {
              handleLogout();
              handleMenuClick();
            }}
          >
            <MdOutlineLogout />
            <span>Sair da conta</span>
          </button>
        ) : null}

        {!isMobile && !usuarioData && (
          <Link to='/login' style={{ width: '100%' }}>
            <button className={styles.buttonAuth} onClick={handleMenuClick}>
              <CiLogin />
              <span>Entrar</span>
            </button>
          </Link>
        )}
      </div>
    </aside>
  );
};

export default SidebarOfficial;
